import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  DollarSign, 
  CheckCircle, 
  Clock, 
  Download, 
  LogOut,
  Filter,
  Trash2,
  UserX,
  Key,
  Mail,
  Plus,
  Pencil,
  X,
  Settings,
  Save
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');
  const [filters, setFilters] = useState({
    captain: 'all',
    level: 'all',
    payment_status: 'all',
    shirt_size: 'all'
  });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: '', id: '', name: '' });
  const [passwordModal, setPasswordModal] = useState({ open: false, userId: '', email: '' });
  const [newPassword, setNewPassword] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, reg: null });
  const emptyForm = {
    full_name: '', phone: '', email: '',
    partner_name: '', level: 'Iniciante',
    payment_status: 'pago',
  };
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [eventConfig, setEventConfig] = useState(null);
  const [configForm, setConfigForm] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/admin/login');
      return;
    }
    fetchData();
    fetchEventConfig();
  }, [filters]);

  const fetchEventConfig = async () => {
    try {
      const res = await axios.get(`${API}/event-config`);
      setEventConfig(res.data);
      setConfigForm(res.data);
    } catch (e) {
      // silent
    }
  };

  const handleSaveConfig = async () => {
    if (!configForm) return;
    if (!configForm.event_name?.trim()) {
      toast.error('Nome do evento é obrigatório');
      return;
    }
    if (configForm.price < 0 || Number.isNaN(Number(configForm.price))) {
      toast.error('Preço inválido');
      return;
    }
    if (!configForm.categories || configForm.categories.length === 0) {
      toast.error('Informe ao menos uma categoria');
      return;
    }
    setSavingConfig(true);
    try {
      const payload = {
        event_name: configForm.event_name.trim(),
        tagline: configForm.tagline?.trim() || '',
        duration_text: configForm.duration_text?.trim() || '',
        price: Number(configForm.price),
        categories: configForm.categories,
        registrations_open: !!configForm.registrations_open,
        event_year: configForm.event_year?.toString().trim() || '',
      };
      const res = await axios.patch(`${API}/admin/event-config`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEventConfig(res.data);
      setConfigForm(res.data);
      toast.success('Configurações salvas!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar configurações');
    } finally {
      setSavingConfig(false);
    }
  };

  const addCategory = () => {
    const cat = (newCategory || '').trim();
    if (!cat) return;
    if (configForm.categories.includes(cat)) {
      toast.error('Categoria já existe');
      return;
    }
    setConfigForm({ ...configForm, categories: [...configForm.categories, cat] });
    setNewCategory('');
  };

  const removeCategory = (cat) => {
    setConfigForm({
      ...configForm,
      categories: configForm.categories.filter((c) => c !== cat),
    });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, regsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/registrations`, {
          params: {
            captain: (filters.captain && filters.captain !== 'all') ? filters.captain : undefined,
            level: (filters.level && filters.level !== 'all') ? filters.level : undefined,
            payment_status: (filters.payment_status && filters.payment_status !== 'all') ? filters.payment_status : undefined,
            shirt_size: (filters.shirt_size && filters.shirt_size !== 'all') ? filters.shirt_size : undefined
          },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRegistrations(regsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'inscricoes_challenge_run.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Arquivo exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    }
  };

  const handleUpdatePayment = async (registrationId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/registrations/${registrationId}/payment`,
        { payment_status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status atualizado!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleDeleteRegistration = async () => {
    try {
      await axios.delete(
        `${API}/admin/registrations/${deleteModal.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inscrição excluída com sucesso!');
      setDeleteModal({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir inscrição');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(
        `${API}/admin/users/${deleteModal.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Usuário excluído com sucesso!');
      setDeleteModal({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Senha deve ter no mínimo 6 caracteres');
      return;
    }

    try {
      await axios.patch(
        `${API}/admin/users/${passwordModal.userId}/password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Senha alterada com sucesso!');
      setPasswordModal({ open: false, userId: '', email: '' });
      setNewPassword('');
    } catch (error) {
      toast.error('Erro ao alterar senha');
    }
  };

  const handleCreateRegistration = async () => {
    if (!createForm.full_name || !createForm.phone || !createForm.partner_name?.trim()) {
      toast.error('Nome, telefone e nome da dupla são obrigatórios');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: createForm.full_name.trim(),
        phone: createForm.phone.trim(),
        partner_name: createForm.partner_name.trim(),
        level: createForm.level,
        payment_status: createForm.payment_status,
      };
      if (createForm.email && createForm.email.trim()) {
        payload.email = createForm.email.trim();
      }
      await axios.post(`${API}/admin/registrations`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inscrição criada com sucesso!');
      setCreateModal(false);
      setCreateForm(emptyForm);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar inscrição');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (reg) => {
    setEditForm({
      full_name: reg.full_name || '',
      phone: reg.phone || '',
      level: reg.level || 'Iniciante',
      payment_status: reg.payment_status || 'pendente',
      partner_name: reg.partner_name || '',
      km_base: reg.km_base ?? 0,
      km_bonus: reg.km_bonus ?? 0,
    });
    setEditModal({ open: true, reg });
  };

  const handleUpdateRegistration = async () => {
    if (!editModal.reg) return;
    setSubmitting(true);
    try {
      const payload = {
        full_name: editForm.full_name.trim(),
        phone: editForm.phone.trim(),
        level: editForm.level,
        payment_status: editForm.payment_status,
      };
      if (editForm.partner_name !== undefined && editForm.partner_name !== null && String(editForm.partner_name).trim()) {
        payload.partner_name = String(editForm.partner_name).trim();
      }
      if (editForm.km_base !== undefined && editForm.km_base !== '') {
        payload.km_base = Number(editForm.km_base);
      }
      if (editForm.km_bonus !== undefined && editForm.km_bonus !== '') {
        payload.km_bonus = Number(editForm.km_bonus);
      }
      await axios.patch(
        `${API}/admin/registrations/${editModal.reg.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Inscrição atualizada!');
      setEditModal({ open: false, reg: null });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar inscrição');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendEmail = async (registrationId, fullName) => {
    const customEmail = window.prompt(
      `Reenviar e-mail de confirmação para "${fullName}".\n\nDigite um e-mail (ou deixe em branco para usar o e-mail cadastrado da conta):`,
      ''
    );
    if (customEmail === null) return;

    try {
      const payload = customEmail.trim() ? { email: customEmail.trim() } : {};
      const res = await axios.post(
        `${API}/admin/registrations/${registrationId}/resend-email`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message || 'E-mail reenviado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reenviar e-mail');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-xl font-unbounded">Carregando...</div>
      </div>
    );
  }

  const teamColors = {
    'Time Verde': '#C5FF00',
    'Time Preto': '#1A1A1A',
    'Time Branco': '#FFFFFF'
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-white/10 bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-unbounded font-bold text-2xl md:text-3xl tracking-tight uppercase">
                <span className="text-white">Admin</span>{' '}
                <span className="text-primary">Dashboard</span>
              </h1>
              <p className="text-muted-foreground text-sm mt-1" data-testid="dashboard-subtitle">{eventConfig?.event_name || 'R77 Challenge 7.0'}</p>
            </div>
            <div className="flex items-center gap-4">
              <Button
                data-testid="export-button"
                onClick={handleExport}
                variant="outline"
                className="border-white/20 hover:border-primary hover:text-primary"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="text-muted-foreground hover:text-white"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/10 bg-card">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('registrations')}
              className={`py-4 font-unbounded text-sm uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === 'registrations'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-white'
              }`}
            >
              Inscrições
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`py-4 font-unbounded text-sm uppercase tracking-wider border-b-2 transition-colors ${
                activeTab === 'users'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-white'
              }`}
            >
              Usuários
            </button>
            <button
              data-testid="event-config-tab"
              onClick={() => setActiveTab('config')}
              className={`py-4 font-unbounded text-sm uppercase tracking-wider border-b-2 transition-colors flex items-center gap-2 ${
                activeTab === 'config'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-white'
              }`}
            >
              <Settings className="w-4 h-4" />
              Configurações
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {activeTab !== 'config' && (
          <>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <div className="text-3xl font-bold mb-1">{stats?.total_registrations || 0}</div>
            <div className="text-muted-foreground text-sm font-mono uppercase tracking-wider">Inscrições</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <div className="text-3xl font-bold mb-1">{stats?.total_paid || 0}</div>
            <div className="text-muted-foreground text-sm font-mono uppercase tracking-wider">Pagos</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <Clock className="w-8 h-8 text-primary" />
            </div>
            <div className="text-3xl font-bold mb-1">{stats?.total_pending || 0}</div>
            <div className="text-muted-foreground text-sm font-mono uppercase tracking-wider">Pendentes</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-white/10 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <DollarSign className="w-8 h-8 text-primary" />
            </div>
            <div className="text-3xl font-bold mb-1">R$ {stats?.amount_collected?.toFixed(2) || '0.00'}</div>
            <div className="text-muted-foreground text-sm font-mono uppercase tracking-wider">Arrecadado</div>
          </motion.div>
        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {Object.entries(stats?.teams || {}).map(([color, count]) => (
            <div
              key={color}
              className="bg-card border border-white/10 p-4"
            >
              <div
                className="w-8 h-8 rounded-full mb-2"
                style={{ backgroundColor: teamColors[color] }}
              />
              <div className="text-2xl font-bold">{count}</div>
              <div className="text-muted-foreground text-sm capitalize">{color}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-white/10 p-6 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Filter className="w-5 h-5 text-primary" />
            <h2 className="font-unbounded font-bold text-lg">Filtros</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                Capitão
              </Label>
              <Select
                value={filters.captain}
                onValueChange={(value) => setFilters({ ...filters, captain: value })}
              >
                <SelectTrigger data-testid="captain-filter" className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder="Todos os capitães" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Denilson">Denilson</SelectItem>
                  <SelectItem value="Roselma">Roselma</SelectItem>
                  <SelectItem value="Rilary">Rilary</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                Nível
              </Label>
              <Select
                value={filters.level}
                onValueChange={(value) => setFilters({ ...filters, level: value })}
              >
                <SelectTrigger data-testid="level-filter" className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder="Todos os níveis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="Iniciante">Iniciante</SelectItem>
                  <SelectItem value="Intermediário">Intermediário</SelectItem>
                  <SelectItem value="Avançado">Avançado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                Tamanho Camisa
              </Label>
              <Select
                value={filters.shirt_size}
                onValueChange={(value) => setFilters({ ...filters, shirt_size: value })}
              >
                <SelectTrigger data-testid="shirt-filter" className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder="Todos os tamanhos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="P">P</SelectItem>
                  <SelectItem value="M">M</SelectItem>
                  <SelectItem value="G">G</SelectItem>
                  <SelectItem value="GG">GG</SelectItem>
                  <SelectItem value="XG">XG</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                Status de Pagamento
              </Label>
              <Select
                value={filters.payment_status}
                onValueChange={(value) => setFilters({ ...filters, payment_status: value })}
              >
                <SelectTrigger data-testid="payment-filter" className="bg-secondary/50 border-white/10">
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pago">Pago</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
          </>
        )}

        {/* Content based on active tab */}
        {activeTab === 'registrations' && (
          <>
            {/* Registrations Table */}
            <div className="bg-card border border-white/10 overflow-hidden">
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h2 className="font-unbounded font-bold text-xl">Inscrições</h2>
                <Button
                  data-testid="open-create-registration-btn"
                  onClick={() => { setCreateForm(emptyForm); setCreateModal(true); }}
                  className="bg-primary text-black font-unbounded font-bold uppercase tracking-wider hover:bg-primary/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Inscrição
                </Button>
              </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Nome</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Telefone</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Capitão</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Time</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Nível</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Camisa</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Valor</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Status Ação</th>
                  <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Excluir</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {registrations.map((reg) => (
                  <tr key={reg.id} className="hover:bg-secondary/20 transition-colors">
                    <td className="px-6 py-4 text-sm">{reg.full_name}</td>
                    <td className="px-6 py-4 text-sm font-mono">{reg.phone}</td>
                    <td className="px-6 py-4 text-sm font-bold">{reg.captain || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ 
                            backgroundColor: teamColors[reg.team] || (reg.team_color === 'verde' ? '#C5FF00' : reg.team_color === 'azul' ? '#00F0FF' : reg.team_color === 'laranja' ? '#FF5F00' : '#FFE600'),
                            border: (reg.team === 'Time Branco') ? '1px solid rgba(197, 255, 0, 0.5)' : 'none'
                          }}
                        />
                        <span className="text-sm capitalize">{reg.team || reg.team_color || '-'}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm">{reg.level || '-'}</td>
                    <td className="px-6 py-4 text-sm">
                      {reg.with_shirt ? `Sim (${reg.shirt_size})` : 'Não'}
                    </td>
                    <td className="px-6 py-4 text-sm font-mono">R$ {reg.amount.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-mono uppercase tracking-wider ${
                        reg.payment_status === 'pago'
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'bg-muted text-muted-foreground border border-white/10'
                      }`}>
                        {reg.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Select
                        value={reg.payment_status}
                        onValueChange={(value) => handleUpdatePayment(reg.id, value)}
                      >
                        <SelectTrigger className="w-32 bg-secondary/50 border-white/10 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pago">Pago</SelectItem>
                          <SelectItem value="pendente">Pendente</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Button
                          data-testid={`edit-registration-${reg.id}`}
                          onClick={() => openEdit(reg)}
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:text-primary/80 hover:bg-primary/10"
                          title="Editar inscrição"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`resend-email-${reg.id}`}
                          onClick={() => handleResendEmail(reg.id, reg.full_name)}
                          variant="ghost"
                          size="sm"
                          className="text-primary hover:text-primary/80 hover:bg-primary/10"
                          title="Reenviar e-mail de confirmação"
                        >
                          <Mail className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => setDeleteModal({ 
                            open: true, 
                            type: 'registration', 
                            id: reg.id, 
                            name: reg.full_name 
                          })}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          title="Excluir inscrição"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {registrations.length === 0 && (
            <div className="p-12 text-center text-muted-foreground">
              Nenhuma inscrição encontrada
            </div>
          )}
            </div>
          </>
        )}

        {/* Users Management Tab */}
        {activeTab === 'users' && (
          <div className="bg-card border border-white/10 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="font-unbounded font-bold text-xl">Gerenciar Usuários</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-secondary/30">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Nome</th>
                    <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Telefone</th>
                    <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Admin</th>
                    <th className="px-6 py-4 text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {users.filter(u => u.email !== 'admin@challengerun.com').map((user) => (
                    <tr key={user.id} className="hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-4 text-sm font-mono">{user.email}</td>
                      <td className="px-6 py-4 text-sm">{user.full_name}</td>
                      <td className="px-6 py-4 text-sm font-mono">{user.phone}</td>
                      <td className="px-6 py-4 text-sm">
                        {user.is_admin ? (
                          <span className="text-primary">Sim</span>
                        ) : (
                          <span className="text-muted-foreground">Não</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => setPasswordModal({ 
                              open: true, 
                              userId: user.id, 
                              email: user.email 
                            })}
                            variant="ghost"
                            size="sm"
                            className="text-primary hover:text-primary/80 hover:bg-primary/10"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => setDeleteModal({ 
                              open: true, 
                              type: 'user', 
                              id: user.id, 
                              name: user.email 
                            })}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                          >
                            <UserX className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.filter(u => u.email !== 'admin@challengerun.com').length === 0 && (
              <div className="p-12 text-center text-muted-foreground">
                Nenhum usuário cadastrado
              </div>
            )}
          </div>
        )}

        {/* Event Configuration Tab */}
        {activeTab === 'config' && configForm && (
          <div className="bg-card border border-white/10 p-6 md:p-8 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="w-6 h-6 text-primary" />
              <h2 className="font-unbounded font-bold text-xl">Configurações do Evento</h2>
            </div>
            <p className="text-muted-foreground text-sm mb-8">
              Edite aqui os dados que aparecem na página inicial e no formulário de inscrição. As alterações são aplicadas em tempo real.
            </p>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Nome do evento</label>
                  <Input
                    data-testid="config-event-name"
                    value={configForm.event_name || ''}
                    onChange={(e) => setConfigForm({ ...configForm, event_name: e.target.value })}
                    placeholder="R77 Challenge 7.0"
                    className="bg-secondary/30 border-white/10"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Ano</label>
                  <Input
                    data-testid="config-event-year"
                    value={configForm.event_year || ''}
                    onChange={(e) => setConfigForm({ ...configForm, event_year: e.target.value })}
                    placeholder="2026"
                    className="bg-secondary/30 border-white/10"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Slogan / subtítulo</label>
                <textarea
                  data-testid="config-tagline"
                  value={configForm.tagline || ''}
                  onChange={(e) => setConfigForm({ ...configForm, tagline: e.target.value })}
                  rows={2}
                  placeholder="Você e sua dupla em 21 dias de desafio..."
                  className="w-full bg-secondary/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-primary focus:outline-none resize-none"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Duração (texto)</label>
                  <Input
                    data-testid="config-duration"
                    value={configForm.duration_text || ''}
                    onChange={(e) => setConfigForm({ ...configForm, duration_text: e.target.value })}
                    placeholder="21 dias"
                    className="bg-secondary/30 border-white/10"
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Preço da inscrição (R$)</label>
                  <Input
                    data-testid="config-price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={configForm.price ?? 0}
                    onChange={(e) => setConfigForm({ ...configForm, price: e.target.value })}
                    className="bg-secondary/30 border-white/10"
                  />
                </div>
              </div>

              <div>
                <label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">Categorias</label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {configForm.categories?.map((cat) => (
                    <span
                      key={cat}
                      data-testid={`config-category-${cat}`}
                      className="inline-flex items-center gap-2 bg-primary/10 border border-primary/30 text-primary px-3 py-1.5 text-sm font-mono"
                    >
                      {cat}
                      <button
                        onClick={() => removeCategory(cat)}
                        className="hover:text-red-400 transition-colors"
                        aria-label={`Remover ${cat}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="config-new-category"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                    placeholder="Nova categoria (ex: Elite)"
                    className="bg-secondary/30 border-white/10"
                  />
                  <Button
                    data-testid="config-add-category-btn"
                    onClick={addCategory}
                    variant="outline"
                    className="border-white/20 hover:border-primary hover:text-primary shrink-0"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Adicionar
                  </Button>
                </div>
              </div>

              <div className="bg-secondary/20 border border-white/10 p-4 flex items-center justify-between">
                <div>
                  <p className="font-unbounded font-bold text-sm">Inscrições abertas</p>
                  <p className="text-muted-foreground text-xs mt-1">
                    Desligue para bloquear novas inscrições. Landing mostra "Inscrições encerradas".
                  </p>
                </div>
                <button
                  data-testid="config-registrations-open-toggle"
                  onClick={() => setConfigForm({ ...configForm, registrations_open: !configForm.registrations_open })}
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
                    configForm.registrations_open ? 'bg-primary' : 'bg-white/10'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-black transition-transform ${
                      configForm.registrations_open ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className="pt-4 border-t border-white/10 flex justify-end gap-3">
                <Button
                  onClick={() => setConfigForm(eventConfig)}
                  variant="outline"
                  className="border-white/20"
                  disabled={savingConfig}
                >
                  Cancelar alterações
                </Button>
                <Button
                  data-testid="config-save-btn"
                  onClick={handleSaveConfig}
                  disabled={savingConfig}
                  className="bg-primary text-black hover:bg-primary/90 font-bold"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingConfig ? 'Salvando...' : 'Salvar Configurações'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal.open && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-white/10 p-8 max-w-md w-full"
            >
              <h3 className="font-unbounded font-bold text-xl mb-4">
                Confirmar Exclusão
              </h3>
              <p className="text-muted-foreground mb-6">
                Tem certeza que deseja excluir {deleteModal.type === 'registration' ? 'a inscrição de' : 'o usuário'}{' '}
                <span className="text-primary font-bold">{deleteModal.name}</span>?
                {deleteModal.type === 'user' && ' Isso também excluirá a inscrição associada.'}
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={() => setDeleteModal({ open: false, type: '', id: '', name: '' })}
                  variant="outline"
                  className="flex-1 border-white/20"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={deleteModal.type === 'registration' ? handleDeleteRegistration : handleDeleteUser}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                >
                  Excluir
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Password Reset Modal */}
        {passwordModal.open && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-card border border-white/10 p-8 max-w-md w-full"
            >
              <h3 className="font-unbounded font-bold text-xl mb-4">
                Redefinir Senha
              </h3>
              <p className="text-muted-foreground mb-6">
                Usuário: <span className="text-primary font-bold">{passwordModal.email}</span>
              </p>
              <div className="mb-6">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Nova Senha
                </Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-secondary/50 border-white/10 focus:border-primary h-12"
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="flex gap-4">
                <Button
                  onClick={() => {
                    setPasswordModal({ open: false, userId: '', email: '' });
                    setNewPassword('');
                  }}
                  variant="outline"
                  className="flex-1 border-white/20"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleResetPassword}
                  className="flex-1 bg-primary text-black hover:bg-primary/90"
                  disabled={newPassword.length < 6}
                >
                  Alterar Senha
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Create / Edit Registration Modal */}
        {(createModal || editModal.open) && (
          <RegistrationFormModal
            isEdit={editModal.open}
            form={editModal.open ? editForm : createForm}
            setForm={editModal.open ? setEditForm : setCreateForm}
            onSubmit={editModal.open ? handleUpdateRegistration : handleCreateRegistration}
            onClose={() => { setCreateModal(false); setEditModal({ open: false, reg: null }); }}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  );
};

const RegistrationFormModal = ({ isEdit, form, setForm, onSubmit, onClose, submitting }) => {
  const upd = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card border border-white/10 p-6 max-w-2xl w-full my-8"
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-unbounded font-bold text-2xl">
            {isEdit ? 'Editar Inscrição' : 'Nova Inscrição'}
          </h3>
          <button
            data-testid="close-registration-modal"
            onClick={onClose}
            className="text-muted-foreground hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Nome completo *</Label>
            <Input
              data-testid="reg-form-name"
              value={form.full_name}
              onChange={(e) => upd('full_name', e.target.value)}
              placeholder="Nome do atleta"
              className="bg-secondary/30 border-white/10"
            />
          </div>
          <div>
            <Label>Telefone *</Label>
            <Input
              data-testid="reg-form-phone"
              value={form.phone}
              onChange={(e) => upd('phone', e.target.value)}
              placeholder="91999999999"
              className="bg-secondary/30 border-white/10"
            />
          </div>
          {!isEdit && (
            <div>
              <Label>E-mail (opcional)</Label>
              <Input
                data-testid="reg-form-email"
                type="email"
                value={form.email}
                onChange={(e) => upd('email', e.target.value)}
                placeholder="email@exemplo.com"
                className="bg-secondary/30 border-white/10"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <Label>Nome da dupla *</Label>
            <Input
              data-testid="reg-form-partner-name"
              value={form.partner_name || ''}
              onChange={(e) => upd('partner_name', e.target.value)}
              placeholder="Ex: Tico e Teco"
              className="bg-secondary/30 border-white/10"
            />
          </div>
          <div>
            <Label>Nível *</Label>
            <Select value={form.level} onValueChange={(v) => upd('level', v)}>
              <SelectTrigger data-testid="reg-form-level" className="bg-secondary/30 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Iniciante">Iniciante</SelectItem>
                <SelectItem value="Intermediário">Intermediário</SelectItem>
                <SelectItem value="Avançado">Avançado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status de pagamento</Label>
            <Select value={form.payment_status} onValueChange={(v) => upd('payment_status', v)}>
              <SelectTrigger data-testid="reg-form-status" className="bg-secondary/30 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="pendente">Pendente</SelectItem>
                <SelectItem value="recusado">Recusado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isEdit && (
            <>
              <div className="md:col-span-2 mt-2">
                <Label className="font-mono text-xs uppercase tracking-wider text-primary">Pontuação do atleta (Ranking)</Label>
                <p className="text-xs text-muted-foreground mt-1">Os KMs são por atleta. O ranking soma os KMs dos dois membros da dupla.</p>
              </div>
              <div>
                <Label>KM Base corridos</Label>
                <Input
                  data-testid="reg-form-km-base"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.km_base ?? 0}
                  onChange={(e) => upd('km_base', e.target.value)}
                  placeholder="0"
                  className="bg-secondary/30 border-white/10"
                />
              </div>
              <div>
                <Label>KM Bônus</Label>
                <Input
                  data-testid="reg-form-km-bonus"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.km_bonus ?? 0}
                  onChange={(e) => upd('km_bonus', e.target.value)}
                  placeholder="0"
                  className="bg-secondary/30 border-white/10"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1 border-white/20"
            disabled={submitting}
          >
            Cancelar
          </Button>
          <Button
            data-testid="submit-registration-form"
            onClick={onSubmit}
            disabled={submitting}
            className="flex-1 bg-primary text-black hover:bg-primary/90 font-bold"
          >
            {submitting ? 'Salvando...' : (isEdit ? 'Salvar Alterações' : 'Criar Inscrição')}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

const Label = ({ children, className, ...props }) => (
  <label className={className} {...props}>{children}</label>
);

export default Dashboard;
